<template>
  <el-dialog
    v-model="visible"
    width="760px"
    @close="onClose"
    append-to-body
    destroy-on-close
    class="appointment-modal">
    <div class="appointment-modal-body">
    <div class="appointment-header-visual">
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <Calendar />
          </el-icon>
          <span>{{ formatDate(appointment.date) }}</span>
          <span style="color: var(--ui-color-text-soft);">
            ({{ formattedDate(appointment.date) }}
            <span
              class="date-weekday"
              :class="{ weekend: isWeekend }"
            >
            {{ weekdayLabel }}
          </span>)
          </span>
        </div>
        <div class="header-item">
          <el-icon>
            <Clock />
          </el-icon>
          <span>{{ appointment.start_time }} – {{ appointment.end_time }}</span>
        </div>
      </div>
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <UserFilled />
          </el-icon>
          <span>{{ appointment.user_name }}</span>
        </div>
        <div class="header-item">
          <el-icon>
            <OfficeBuilding />
          </el-icon>
          <span>{{ appointment.room_name }}</span>
        </div>
      </div>
      <div class="header-row status-row">
        <div class="header-item">
          <el-icon>
            <AlarmClock />
          </el-icon>
          <span>{{ appointment.duration_time || totalDuration }} мин</span>
        </div>
      </div>
    </div>

    <div v-if="!clientIsDeleted" class="client-info-card">
      <div class="client-phone" v-if="appointment.client_phone">
        <el-icon>
          <Phone />
        </el-icon>
        <a :href="`tel:${appointment.client_phone}`" class="phone-link">{{ appointment.client_phone }}</a>
        <el-tooltip content="Скопировать" placement="top">
          <el-button :icon="CopyDocument" text @click="copyPhone" class="copy-button" circle />
        </el-tooltip>
      </div>
      <div class="client-name" @click="showClientProfile" title="Показать профиль клиента">
        <el-icon>
          <UserFilled />
        </el-icon>
        <span class="name-text">{{ appointment.client_name }}</span>
        <el-button :icon="Edit" text circle @click.stop="showClientProfile" class="edit-button"
                   title="Редактировать клиента" />
      </div>
    </div>
    <div v-else class="client-info-card client-deleted">
      <el-icon>
        <UserFilled />
      </el-icon>
      <span style="color:#aaa;font-size:15px;margin-left:4px;">Клиент удалён или не найден</span>
    </div>

    <el-table :data="appointment.services" size="small" border v-if="appointment.services.length"
              style="margin-top: 10px;" show-overflow-tooltip>
      <el-table-column prop="name" label="Услуга" />
      <el-table-column prop="duration" label="Мин." width="70" />
      <el-table-column prop="price" label="Сумма" width="90" />
    </el-table>
    <div v-if="appointment.services.length" class="services-total">
      <span>Общая сумма:</span>
      <b>{{ appointment.total_sum }} ₽</b>
    </div>

    <div class="appointment-view-content">
      <div class="appointment-view-row" v-if="appointment.comment">
        <el-icon>
          <Comment />
        </el-icon>
        <span style="margin-left: 10px">{{ appointment.comment }}</span>
      </div>
    </div>
    </div>
    <template #footer>
      <el-row>
        <el-col :span="24" style="text-align: right;">
          <template v-if="appointment.status === 'done'">
            <el-button
              type="warning"
              @click="cancelCompletion">
              Отменить выполнение
            </el-button>
          </template>
          <template v-else>
            <div class="footer-actions">
              <el-dropdown @command="changeStatus" trigger="click" v-if="hasPermission('appointments.edit')">
                <span
                  class="status-text"
                  :style="{ color: STATUS_MAP[appointment.status]?.color }">
                  <font-awesome-icon
                    v-if="STATUS_MAP[appointment.status]?.icon"
                    :icon="STATUS_MAP[appointment.status].icon"
                    style="margin-right:6px;font-size:17px;"
                  />
                  {{ STATUS_MAP[appointment.status]?.label || appointment.status }}
                  <el-icon style="margin-left:4px; vertical-align: middle; position: relative;"><ArrowDown /></el-icon>
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item
                      v-for="(obj, key) in STATUS_MAP"
                      :key="key"
                      :command="key"
                      :disabled="appointment.status === key"
                      :style="{ color: obj.color }"
                    >
                      {{ obj.label }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              <el-button
                v-if="hasPermission('appointments.edit')"
                style="margin-left: 20px"
                circle type="primary"
                @click="onEdit"
                title="Редактировать">
                <el-icon><Edit /></el-icon>
              </el-button>
              <el-button
                v-if="hasPermission('appointments.delete')"
                circle type="danger"
                @click="onDelete"
                title="Удалить">
                <el-icon><Delete /></el-icon>
              </el-button>
              <el-button
                v-if="hasPermission('appointments.complete') && !['no_show', 'done', 'canceled'].includes(appointment.status)"
                type="success"
                @click="openClosed">
                Выполнить
              </el-button>
            </div>
          </template>
        </el-col>
      </el-row>
    </template>
  </el-dialog>
</template>

<script>
import {
  AlarmClock,
  ArrowDown,
  Calendar,
  Clock,
  Comment,
  CopyDocument,
  Delete,
  Edit,
  OfficeBuilding,
  Phone,
  UserFilled
} from '@element-plus/icons-vue'
import { mapState } from 'vuex'
import { hasPermission } from '@/components/util/accessHelper'

export default {
  name: 'AppointmentViewModal',
  components: {
    Delete,
    Comment,
    AlarmClock,
    Clock,
    OfficeBuilding,
    Calendar,
    UserFilled,
    Phone,
    Edit,
    ArrowDown
  },
  props: {
    date: { type: String, required: true },
    appointment: { type: Object, required: true }
  },
  data() {
    return {
      showCompleteModal: false,
      visible: true,
      STATUS_MAP: {
        planned: { label: 'Запланировано', color: 'var(--ui-color-warning)', icon: ['fas', 'clock'] },
        confirmed: { label: 'Подтверждено', color: 'var(--ui-color-brand)', icon: ['fas', 'circle-check'] },
        no_show: { label: 'Не пришёл', color: 'var(--ui-color-danger-el)', icon: ['fas', 'ban'] },
        canceled: { label: 'Отменено', color: 'var(--ui-color-text-disabled)', icon: ['fas', 'trash-can'] }
      }
    }
  },
  computed: {
    ...mapState('custom_fields', ['fields']),
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return ''
      const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
      return weekdays[d.getDay()]
    },
    isWeekend() {
      if (!this.date) return false
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return false
      return d.getDay() === 0 || d.getDay() === 6
    },
    CopyDocument() {
      return CopyDocument
    },
    Edit() {
      return Edit
    },
    clientIsDeleted() {
      // Если нет имени и нет телефона — считаем "удалён"
      const name = (this.appointment.client_name || '').trim()
      const phone = (this.appointment.client_phone || '').trim()
      // id может быть, но если имя и телефон пусты — это "удалён"
      return !name && !phone
    },
    totalDuration() {
      // если нет appointment.duration_time, считаем по услугам
      if (this.appointment && this.appointment.services && this.appointment.services.length) {
        return this.appointment.services.reduce((sum, s) => sum + (s.duration || 0), 0)
      }
      return 0
    }
  },
  methods: {
    hasPermission,
    formattedDate(date) {
      const months = [
        'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
        'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
      ]
      // YYYY-MM-DD
      const [, month, day] = date.split('-')
      const dayStr = day.length === 1 ? `0${day}` : day
      const monthName = months[parseInt(month, 10) - 1]
      return `${dayStr} ${monthName}`
    },
    formatDate(date) {
      if (!date) return ''
      // формат "2025-06-06" → "06.06.2025"
      const parts = date.split('-')
      if (parts.length !== 3) return date
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    },
    onClose() {
      this.visible = false
      this.$emit('close')
    },
    onEdit() {
      this.$emit('edit', this.appointment)
    },
    showClientProfile() {
      if (this.appointment.client_id) {
        this.$emit('show-client', this.appointment.client_id)
      }
    },
    copyPhone() {
      if (this.appointment.client_phone) {
        navigator.clipboard.writeText(this.appointment.client_phone).then(() => {
          this.$message.success('Скопировано')
        }).catch(() => {
          alert('Не удалось скопировать')
        })
      }
    },
    changeStatus(newStatus) {
      this.$confirm(`Вы уверены, что хотите сменить статус на "${this.STATUS_MAP[newStatus].label}"?`, 'Смена статуса', {
        confirmButtonText: 'Да',
        cancelButtonText: 'Отмена',
        type: 'warning'
      }).then(() => {
        // Здесь вызов API на обновление статуса
        // this.updateStatus(newStatus)
        const data = {
          id: this.appointment.id,
          status: newStatus,
          date: this.date,
          branch_id: this.appointment.branch_id,
          city_id: this.appointment.city_id,
        };
        this.$store.dispatch('appointments/statusAppointment', data);
        // eslint-disable-next-line vue/no-mutating-props
        this.appointment.status = newStatus;
        this.$message.success('Статус обновлен')
      }).catch(() => {
        // Просто игнорируем отказ — пользователь нажал "Отмена"
      })
    },
    openClosed() {
      this.$emit('close')           // Закрыть предпросмотр
      this.$emit('open-complete')   // Сообщить родителю открыть "Выполнить"
    },
    async cancelCompletion() {
      try {
        await this.$confirm('Отменить выполнение и вернуть запись в активные?', 'Подтверждение', {
          confirmButtonText: 'Да',
          cancelButtonText: 'Отмена',
          type: 'warning'
        })
        await this.$store.dispatch('appointments/cancelCompletion', {
          appointment_id: this.appointment.id,
          account_id: this.appointment.account_id,
          date: this.date,
          branch_id: this.appointment.branch_id,
          city_id: this.appointment.city_id,
        })
        // --- Добавлено для реактивного обновления ---
        // eslint-disable-next-line vue/no-mutating-props
        this.appointment.status = 'planned'
        this.$emit('refresh-appointments')
        this.$message.success('Выполнение отменено, средства возвращены')
      } catch (err) {
        // отмена пользователем
      }
    },
    onDelete() {
      this.$confirm('Вы уверены, что хотите удалить эту запись?', 'Подтверждение удаления', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning'
      }).then(() => {
        // Отправляем запрос в store
        this.$store.dispatch('appointments/deleteAppointment', {
          id: this.appointment.id,
          date: this.appointment.date,
          city_id: this.appointment.city_id,
          branch_id: this.appointment.branch_id
        })
          .then(() => {
            this.$message.success('Запись удалена')
            this.$emit('deleted', this.appointment.id)
            this.onClose()
          })
          .catch(() => {
            this.$message.error('Ошибка при удалении записи')
          })
      }).catch(() => {
        // Пользователь отменил удаление
      })
    },
  }
}
</script>

<style scoped>
.date-weekday {
  font-size: 15px;
  color: #5a8fef;
  margin-bottom: 3px;
  font-weight: 600;
}
.date-weekday.weekend {
  color: var(--ui-color-danger);
  font-weight: 700;
}
.services-total {
  margin-top: 12px;
  font-size: 16px;
  text-align: right;
}

.services-total b {
  font-size: 18px;
  margin-left: 12px;
}

.appointment-header-visual {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.header-row {
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: space-between;
}

.header-item {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: var(--ui-color-text-body);
  gap: 8px;
}

.status-row {
  margin-top: 4px;
}

.status-item {
  gap: 6px;
  font-size: 14px;
  color: var(--ui-color-text-body);
  align-items: center;
}

.status-circle {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 4px;
  margin-right: 4px;
}

.status-gray {
  background-color: #999999;
}

.status-blue {
  background-color: var(--ui-color-brand);
}

.status-green {
  background-color: var(--ui-color-success);
}

.status-red {
  background-color: var(--ui-color-danger-el);
}

.client-info-card {
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.client-phone {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: var(--ui-color-text-regular);
}

.phone-link {
  color: var(--ui-color-brand);
  text-decoration: none;
}

.phone-link:hover {
  text-decoration: underline;
}

.copy-button {
  padding: 0;
  margin-left: 4px;
  color: var(--ui-color-text-secondary);
}

.client-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  cursor: pointer;
  user-select: none;
}

.name-text {
  user-select: text;
}

.edit-button {
  padding: 0;
  color: var(--ui-color-text-secondary);
}

.edit-button:hover {
  color: var(--ui-color-brand);
}

.appointment-view-content {
  padding: 6px 4px;
}

.appointment-view-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.view-label {
  min-width: 110px;
  color: var(--ui-color-text-soft);
  font-weight: 500;
  font-size: 15px;
}

.service-list {
  padding-left: 18px;
  margin: 0;
}

.status-text {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.client-deleted {
  color: var(--ui-color-text-disabled);
  justify-content: flex-start;
  align-items: center;
  min-height: 34px;
  font-style: italic;
}
.footer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.appointment-modal-body {
  background: linear-gradient(132deg, #264a8f 0%, #10295d 70%);
  border-radius: 12px;
  padding: 16px;
}

:deep(.appointment-modal .el-dialog) {
  background: transparent;
  border-radius: 14px;
  box-shadow: none;
}

:deep(.appointment-modal .el-dialog__header) {
  padding: 0;
  margin: 0;
}

:deep(.appointment-modal .el-dialog__body) {
  padding: 0;
}

:deep(.appointment-modal .el-dialog__footer) {
  padding: 14px 0 0;
}

:deep(.appointment-modal .el-dialog__headerbtn .el-dialog__close) {
  color: #0c1f49;
  font-size: 24px;
}

.header-item,
.appointment-view-row,
.services-total {
  color: #dce8ff;
}

.client-info-card {
  border: 1px solid #2e4f8c;
  background: rgba(10, 26, 63, 0.75);
}

.client-phone,
.client-name,
.phone-link {
  color: #dce8ff;
}

:deep(.el-table) {
  --el-table-bg-color: rgba(10, 26, 63, 0.75);
  --el-table-tr-bg-color: rgba(10, 26, 63, 0.75);
  --el-table-header-bg-color: rgba(16, 39, 82, 0.95);
  --el-table-text-color: #dce8ff;
  --el-table-header-text-color: #dce8ff;
  --el-table-border-color: #294675;
}
</style>
